<template>
  <template v-if="disabled">
    <slot :tooltipId="tooltipId" />
  </template>
  <span
    v-else
    :id
    :class="['inline-block cursor-help', wrapperClass]"
    ref="wrapperRef"
    @mouseenter="openTooltip()"
    @mouseleave="hideTooltipIfNeitherElIsHovered()"
    @focusin="handleFocusIn"
    @focusout="handleFocusOut"
    @keydown="handleKeydown"
  >
    <slot :tooltipId="tooltipId" />
  </span>

  <!-- Screen reader only description (always rendered) -->
  <div :id="tooltipId" class="sr-only">
    <slot name="tooltip" />
  </div>

  <!-- Visible floating tooltip (only rendered when visible) -->
  <Teleport to="body">
    <div
      v-if="showTooltip"
      role="tooltip"
      @mouseleave="hideTooltipIfNeitherElIsHovered()"
      ref="tooltipRef"
      :style="floatingStyles"
      class="z-50"
      :class="$props['class']"
    >
      <slot name="tooltip" />
    </div>
  </Teleport>
</template>
<script setup lang="ts">
import {defineProps, ref, onBeforeUnmount, useId} from 'vue';
import {useFloating, autoUpdate, offset, shift, flip} from '@floating-ui/vue';

const {disabled, wrapperClass} = defineProps<{
  id?: string;
  disabled?: boolean;
  class?: string | Record<string, boolean> | string[];
  wrapperClass?: string | Record<string, boolean> | string[];
}>();

// Define slot props type
interface DefaultSlotProps {
  tooltipId: string;
}

// Expose slot props for TypeScript
defineSlots<{
  default: (props: DefaultSlotProps) => any;
  tooltip: () => any;
}>();

const DELAY = 400;
const tooltipId = useId();
let openTooltipTimeout: NodeJS.Timeout | null = null;
let closeTooltipTimeout: NodeJS.Timeout | null = null;

const showTooltip = ref(false);
const wrapperRef = ref<HTMLElement | null>(null);
const tooltipRef = ref<HTMLElement | null>(null);
const middleware = ref([offset(8), shift(), flip()]);

const {floatingStyles} = useFloating(wrapperRef, tooltipRef, {
  placement: 'bottom',
  middleware,
  whileElementsMounted: autoUpdate,
});

const openTooltip = () => {
  // Delay opening the tooltip to prevent flickering
  if (openTooltipTimeout) clearTimeout(openTooltipTimeout);
  openTooltipTimeout = setTimeout(() => {
    if (!wrapperRef.value) {
      return;
    }
    const wrapperIsHovered = wrapperRef.value.matches(':hover');
    const wrapperIsFocused =
      document.activeElement === wrapperRef.value ||
      wrapperRef.value.contains(document.activeElement);
    if (wrapperIsHovered || wrapperIsFocused) {
      showTooltip.value = true;
    }
  }, DELAY);
};

const hideTooltipIfNeitherElIsHovered = () => {
  // Delay closing the tooltip to prevent flickering
  if (closeTooltipTimeout) clearTimeout(closeTooltipTimeout);
  closeTooltipTimeout = setTimeout(() => {
    if (!wrapperRef.value || !tooltipRef.value) {
      return;
    }

    const wrapperIsHovered = wrapperRef.value.matches(':hover');
    const tooltipIsHovered = tooltipRef.value.matches(':hover');
    const wrapperIsFocused =
      document.activeElement === wrapperRef.value ||
      wrapperRef.value.contains(document.activeElement);
    const tooltipIsFocused =
      document.activeElement === tooltipRef.value ||
      tooltipRef.value.contains(document.activeElement);

    if (!wrapperIsHovered && !tooltipIsHovered && !wrapperIsFocused && !tooltipIsFocused) {
      showTooltip.value = false;
    }
  }, DELAY);
};

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    showTooltip.value = false;
  }
};

const handleFocusIn = (event: FocusEvent) => {
  if (wrapperRef.value && wrapperRef.value.contains(event.target as Node)) {
    openTooltip();
  }
};

const handleFocusOut = (event: FocusEvent) => {
  if (wrapperRef.value && wrapperRef.value.contains(event.target as Node)) {
    hideTooltipIfNeitherElIsHovered();
  }
};

onBeforeUnmount(() => {
  if (closeTooltipTimeout) {
    clearTimeout(closeTooltipTimeout);
  }
  if (openTooltipTimeout) {
    clearTimeout(openTooltipTimeout);
  }
});
</script>
